import React, { useCallback, useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import { Alert, Autocomplete, Stack, TextField, TextFieldProps } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import { FilesPickerModal } from "components/modals/Files-picker/FilesPicker";
import { fileExtensions } from "constants/file";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import {
  useAccountSelector,
  useDateFormatSelect,
  useInvestorsSelector,
  useMeasureParametersSelector,
  useReportsMetaSelector
} from "store";
import {
  CurrencyType,
  DealBase,
  IInvestorBankDetails,
  InvestorWithBank,
  IRound,
  MeasureParameter,
  MonitoringDataFiles,
  Optional,
  UiMeasureDataFiles
} from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray, currencySign } from "../../../constants";
import { DateRange } from "../Date-range/DateRange";
import { AttachFiles } from "../Groups-form/AttachFiles";
// import { FilesTable } from "../Groups-form/FilesTable";
import { MeasureDataFilesPickerParam } from "../Groups-form/GroupsFormContext";
import { FilesTable } from "./FileTable";

// import { FilesTable } from "./FileTable";

const covertToSelect = (item: {
  id: number;
  name: string;
}): {
  key: string;
  value: number;
  text: string;
} => ({
  key: item.id.toString(),
  value: item.id,
  text: item.name
});

export type FieldsState = {
  deal_id: DealBase | null;
  measure_id: MeasureParameter | null;
  investor_id: InvestorWithBank | null;
  bank_detail_id: number | null;
  round_id: number | null;
  date: string | null;
  accounting_date: string | null;
  currency: CurrencyType | null;
  value: string;
  description: string | null;
  effective_range_from: string | null;
  effective_range_to: string | null;
  files: UiMeasureDataFiles | null;
  is_child: boolean;
};

interface Props extends Pick<FormikProps<FieldsState>, "setFieldValue" | "values" | "errors" | "setErrors"> {
  rounds: Optional<IRound, "deal_commitments">[];
  isCreation?: boolean;
  isRoundsDisabled?: boolean;
  filesErrors?: string[] | null;
}

export const SaveOneForm: React.FC<Props> = ({
  values,
  errors,
  setErrors,
  setFieldValue,
  rounds,
  isCreation = false,
  isRoundsDisabled = true,
  filesErrors = null
}) => {
  const {
    deal_id,
    measure_id,
    investor_id,
    bank_detail_id,
    round_id,
    date,
    accounting_date,
    currency,
    value,
    description,
    effective_range_from,
    effective_range_to,
    files,
    is_child
  } = values;

  const dateFormat = useDateFormatSelect();
  const { deals } = useReportsMetaSelector();
  const measures = useMeasureParametersSelector();
  const isEffectiveRange: boolean = useAccountSelector()?.allow_effective_range || false;
  const investors = useInvestorsSelector();
  const roundsToSelect = useMemo(() => rounds?.map(covertToSelect), [rounds]);

  const [measureDataFilesPickerParam, setMeasureDataFilesPickerParam] = useState<MeasureDataFilesPickerParam | null>(
    null
  );
  const isGlobalValueMeasure = Boolean(measures.find((measure) => measure.id === measure_id?.id)?.is_global_value);

  const currencyPrefix = currency ? currencySign[currency as CurrencyType] : undefined;
  const isNegativeAllowed = !(measure_id?.is_absolute || false);

  const isCurrencyType: boolean = useMemo(() => {
    if (!measure_id) {
      return false;
    }
    return !!measure_id.measure_group.is_currency;
  }, [measure_id]);

  const clearError = useCallback(
    (field: keyof FieldsState) => {
      if (!errors[field]) return;
      const errorsCopy = { ...errors };
      delete errorsCopy[field];
      setErrors(errorsCopy);
    },
    [errors]
  );

  const changeWithError = useCallback(
    (field: keyof FieldsState, value: any) => {
      clearError(field);
      setFieldValue(field, value);
      // Invalidate round when unselect deal
      if (field === "deal_id" && !value && round_id) {
        setFieldValue("round_id", null);
      }
      // Clear date error when measure/investor/round changes
      if (field === "measure_id" || field === "investor_id" || field === "round_id") {
        clearError("date");
      }
      // Clear bank account when investor changes
      if (field === "investor_id") {
        setFieldValue("bank_detail_id", null);
      }
    },
    [clearError, setFieldValue, deal_id, round_id]
  );

  const setFiles = async (data: { filesIds?: number[]; attachments?: File[]; files?: MonitoringDataFiles[] }) => {
    await setFieldValue("files", {
      dbFiles:
        data.files?.reduce(
          (acc, file) => {
            acc[crypto.randomUUID()] = file;
            return acc;
          },
          {} as Record<string, MonitoringDataFiles>
        ) || null,
      localFiles:
        data.attachments?.reduce(
          (acc, attachment) => {
            acc[crypto.randomUUID()] = attachment;
            return acc;
          },
          {} as Record<string, File>
        ) || null
    });
  };

  const onDeleteFile = async (localId: string) => {
    const newDbFiles = files?.dbFiles ? { ...files.dbFiles } : null;
    const newLocalFiles = files?.localFiles ? { ...files.localFiles } : null;

    if (newDbFiles && localId in newDbFiles) {
      delete newDbFiles[localId];
    }
    if (newLocalFiles && localId in newLocalFiles) {
      delete newLocalFiles[localId];
    }

    await setFieldValue("files", {
      dbFiles: newDbFiles && Object.keys(newDbFiles).length > 0 ? newDbFiles : null,
      localFiles: newLocalFiles && Object.keys(newLocalFiles).length > 0 ? newLocalFiles : null
    });
  };

  return (
    <>
      {isCreation && (
        <Autocomplete
          options={deals}
          value={deal_id}
          getOptionLabel={(option) => option.title}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
            <li {...props} key={option.id}>
              {option.title}
            </li>
          )}
          onChange={(_, option) => changeWithError("deal_id", option)}
          renderInput={(params) => (
            <TextField
              {...params}
              {...commonInputPtops}
              label="Deal"
              error={!!errors.deal_id}
              helperText={errors.deal_id}
            />
          )}
        />
      )}
      <Autocomplete
        disableClearable
        options={measures}
        value={measure_id as MeasureParameter}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        getOptionLabel={(option) => option.name}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: MeasureParameter) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        onChange={(_, option) => changeWithError("measure_id", option)}
        renderInput={(params) => (
          <TextField
            {...params}
            {...commonInputPtops}
            label="Measure"
            error={!!errors.measure_id}
            helperText={errors.measure_id}
          />
        )}
      />
      {!isGlobalValueMeasure && (
        <Autocomplete
          options={investors}
          value={investor_id}
          getOptionLabel={(option) => option.name}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: InvestorWithBank) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          onChange={(_, option) => changeWithError("investor_id", option)}
          renderInput={(params) => (
            <TextField
              {...params}
              {...commonInputPtops}
              label="Investor"
              error={!!errors.investor_id}
              helperText={errors.investor_id}
            />
          )}
        />
      )}
      {!isGlobalValueMeasure && investor_id && (
        <Autocomplete
          options={investor_id.bank_details || []}
          value={(investor_id.bank_details || []).find((b) => b.id === bank_detail_id) ?? null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            [option.bank_name, option.account_number].filter(Boolean).join(" – ") || `Account #${option.id}`
          }
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IInvestorBankDetails) => (
            <li {...props} key={option.id}>
              {[option.bank_name, option.account_number].filter(Boolean).join(" – ") || `Account #${option.id}`}
            </li>
          )}
          onChange={(_, option) => setFieldValue("bank_detail_id", option?.id ?? null)}
          renderInput={(params) => <TextField {...params} {...commonInputPtops} label="Bank Account" />}
        />
      )}
      {!isGlobalValueMeasure && (
        <Select
          formProps={{ sx: { ":root": { marginBottom: 0 } } }}
          label="Round"
          value={round_id}
          onChange={(e) => changeWithError("round_id", e.target.value)}
          name="round_id"
          disabled={isRoundsDisabled || is_child}
          items={roundsToSelect || []}
          variant="outlined"
          error={!!errors?.round_id}
          helperText={errors?.round_id}
        />
      )}
      <DesktopDatePicker
        format={dateFormat}
        views={["year", "month", "day"]}
        disabled={is_child}
        value={dayjs(date)}
        label="Effective Date"
        onChange={(value) => {
          if (!value) return;
          // if (value) {
          // setFieldValue("date", value.startOf("day").add(moment.duration(14, "hours")).toDate());
          // setFieldValue("date", toIsoMidday(value.toDate())); not allow to add date
          // }
          changeWithError("date", toIsoMidday(value.toISOString()));
        }}
        slotProps={{
          popper: {
            sx: {
              ".MuiPickersCalendarHeader-labelContainer": {
                fontSize: "0.9rem"
              }
            }
          },
          textField: {
            error: !!errors.date || !!(errors as any).monitoring?.[0],
            helperText: errors.date || (errors as any).monitoring?.[0] || "",
            disabled: is_child
          }
        }}
      />
      <DesktopDatePicker
        format={dateFormat}
        views={["year", "month", "day"]}
        value={dayjs(accounting_date)}
        label="Accounting Date"
        onChange={(value) => {
          changeWithError("accounting_date", value ? toIsoMidday(value.toISOString()) : "");
        }}
        slotProps={{
          popper: {
            sx: {
              ".MuiPickersCalendarHeader-labelContainer": {
                fontSize: "0.9rem"
              }
            }
          },
          textField: {
            error: !!errors?.accounting_date,
            helperText: errors?.accounting_date
          },
          field: {
            clearable: true,
            onClear: () => changeWithError("accounting_date", null)
          }
        }}
      />
      {isEffectiveRange && (
        <DateRange
          defaultDateFrom={effective_range_from}
          defaultDateTo={effective_range_to}
          onClear={() => {
            setFieldValue("effective_range_from", null);
            setFieldValue("effective_range_to", null);
          }}
          onSubmit={({ from, to }) => {
            setFieldValue("effective_range_from", from);
            setFieldValue("effective_range_to", to);
          }}
        />
      )}
      {isCurrencyType && (
        <Select
          label="Currency"
          value={currency}
          onChange={(value) => changeWithError("currency", value.target.value)}
          items={currenciesArray}
          variant="outlined"
          disabled={is_child}
          error={!!errors?.currency}
          helperText={errors?.currency}
        />
      )}
      <Stack direction="row" gap="10px">
        <NumberFormat
          fullWidth
          thousandSeparator
          isNumericString
          variant="outlined"
          error={!!errors?.value}
          helperText={errors?.value}
          {...(isCurrencyType && { prefix: currencyPrefix })}
          decimalScale={2}
          value={value}
          label="Value"
          {...commonInputPtops}
          allowNegative={isNegativeAllowed}
          customInput={TextField}
          placeholder="Enter value"
          onValueChange={({ value }) => changeWithError("value", value)}
        />
        <AttachFiles
          isTotal={!investor_id}
          isMeasureGlobalValue={isGlobalValueMeasure}
          files={files}
          setMeasureDataFilesPickerParam={setMeasureDataFilesPickerParam}
          group_id={null}
          measureDataKey={null}
          roundId={round_id}
        />
      </Stack>
      <TextField
        label="Description"
        value={description}
        {...commonInputPtops}
        placeholder="Description"
        onChange={(e) => changeWithError("description", e.target.value)}
      />
      {Boolean(filesErrors?.length) && (
        <Alert severity="error" sx={{ whiteSpace: "pre-line", borderRadius: "8px !important" }}>
          {filesErrors?.join("\n")}
        </Alert>
      )}
      <FilesTable
        files={files || { dbFiles: null, localFiles: null }}
        parameter={measure_id?.name || ""}
        investor={investor_id?.name || ""}
        date={date || ""}
        onDeleteFile={onDeleteFile}
      />

      {measureDataFilesPickerParam && (
        <FilesPickerModal
          filesPickerModalOpen={Boolean(measureDataFilesPickerParam)}
          onClose={() => setMeasureDataFilesPickerParam(null)}
          currentFiles={
            files?.dbFiles
              ? Object.values(files.dbFiles).map((file) => ({
                  id: file.id,
                  upload_type: "section",
                  name: file.name,
                  file_url: file.file_url
                }))
              : []
          }
          attachedFiles={files?.localFiles ? Object.values(files.localFiles) : []}
          filesPickerSubmit={async (data) => {
            await setFiles({
              filesIds: data.filesIds || [],
              attachments: data.attachments || [],
              files:
                data.files?.map((file) => ({
                  name: file.name,
                  id: file.id,
                  file_url: file.file_url || ""
                })) || []
            });
          }}
          title="Add files"
          workspaceTitle="Select or upload files"
          dealId={deal_id?.id || null}
          getFilesOutsideDeal={true}
          selectWithoutFiles={true}
          acceptedFilesExtensions={fileExtensions.map((extension) => `.${extension}`)}
        />
      )}
    </>
  );
};

const commonInputPtops: Pick<TextFieldProps, "sx" | "size" | "InputLabelProps"> = {
  sx: {
    ".MuiOutlinedInput-root": {
      // width: "310px"
    }
  }
};
